<template>
    <div class="preparation-pickup-point-order-page">
        <transition name="loading-screen">
            <LoadingScreen v-if="loading" />
        </transition>
        <template v-if="errors">
            <ErrorsZone :errors="errors" />
        </template>
        <template v-if="loadedOnce && !errors">
            <div class="row mb-4 align-items-center">
                <div class="col-md-6 col-12">
                    <PreparationCustomerOrderCustomerInformation
                        :order="order"
                    />
                </div>
                <div class="col-md-6 col-12">
                    <OrderPreparationProgressBar
                        v-if="order.preparable"
                        :label="order.status_label"
                        :preparation-status="progressCounters"
                    />

                    <div class="float-right mt-4">
                        <button @click="$refs.modalCustomerInformation.showModal()" class="btn lcdd-btn-with-icon btn-outline-dark mx-2">
                            <div class="lcdd-btn-icon">
                                <i class="fad fa-file-user fa-2x"></i>
                            </div>
                            Infos<br />commande
                        </button>

                        <a :href="`/espace-employes/commandes/preparation/commande/${order.id}/bon-livraison/menu`" class="btn lcdd-btn-with-icon btn-outline-dark mx-2">
                            <div class="lcdd-btn-icon">
                                <i class="fad fa-print fa-2x"></i>
                            </div>
                            Imprimer<br />le BL
                        </a>

                        <OrderPreparationMenu
                            :order="order"
                            :cancellation-reasons="cancellationReasons"
                            :products-by-main-category="productsByMainCategory"
                        />

                        <CustomerInformationModal
                            ref="modalCustomerInformation"
                            :order="order"
                        />
                    </div>
                </div>
            </div>

            <PreparationPickupPointProductionOrderDelivery
                :order="order"
            />

            <Comments
                :commentable-type="'App\\Models\\Order'"
                :commentable-id="order.id"
                :comments="order.comments"
            ></Comments>

            <template v-for="categoryAndProducts in productsByMainCategory">
                <div class="row mt-4">
                    <div class="col-md-12">
                        <h3>{{ categoryAndProducts.category.name }}</h3>
                    </div>
                </div>

                <div class="row">
                    <template v-if="orderHaveToBePrepared">
                        <PreparationProduct
                            v-for="(product, sku) in categoryAndProducts.products"
                            class="col-md-4 mb-3"
                            :key="sku"
                            :order="order"
                            :product="product.product"
                            :preparation-status="product.preparation_status"
                            :inputs="product.inputs"
                            :cancellation-reasons="cancellationReasons"
                            :force-by-unit="orderHaveToBePreparedByUnit"
                        >
                            {{ product.product.tiny_name }}
                        </PreparationProduct>
                    </template>
                    <template v-else>
                        <PreparationProductPickupPoint
                            v-for="(product, sku) in categoryAndProducts.products"
                            class="col-md-4 mb-3"
                            :key="sku"
                            :order="order"
                            :sku="product.product.sku"
                            :name="product.product.tiny_name"
                            :preparation-status="product.preparation_status"
                        />
                    </template>
                </div>
            </template>
        </template>
    </div>
</template>

<script>
    import AddPreparedProductModal from "../AddPreparedProductModal";
    import Comments from "../../Comments";
    import CustomerInformationModal from "../CustomerOrder/CustomerInformationModal";
    import ErrorsZone from "../../ErrorsZone";
    import LoadingScreen from "../../LoadingScreen";
    import OrderPreparationMenu from "../OrderPreparationMenu";
    import OrderPreparationProgressBar from "../CustomerOrder/OrderPreparationProgressBar";
    import PreparationCustomerOrderCustomerInformation from "../PreparationCustomerOrderCustomerInformation";
    import PreparationPickupPointProductionOrderDelivery from "./PreparationPickupPointProductionOrderDelivery";
    import PreparationProduct from "../PreparationProduct";
    import PreparationProductPickupPoint from "./PreparationProductPickupPoint";

    import {useGetLoader} from "../../compositions/axios/loadingRequest";
    import {usePostRequest} from "../../compositions/axios/postRequest"
    import {orderDone} from "../../../orders-utils"

    export default {
        name: 'PreparationPickupPointProductionOrderPage',

        components: {
            AddPreparedProductModal,
            Comments,
            CustomerInformationModal,
            ErrorsZone,
            LoadingScreen,
            OrderPreparationMenu,
            OrderPreparationProgressBar,
            PreparationCustomerOrderCustomerInformation,
            PreparationPickupPointProductionOrderDelivery,
            PreparationProduct,
            PreparationProductPickupPoint,
        },

        props: {
            orderId: {
                type: String,
                required: false,
            },
        },

        setup() {
            const {
                loaded,
                loading,
                errors,
                load,
            } = useGetLoader();

            const {
                loaded: sent,
                loading: sending,
                errors: errorsSending,
                post,
            } = usePostRequest();

            return {
                loaded,
                loading,
                errors,
                load,

                sent,
                sending,
                errorsSending,
                post,
            };
        },

        data() {
            return {
                order: {},
                productsByMainCategory: [],
                cancellationReasons: [],
                progressCounters: {
                    nb_ordered: 0,
                    nb_prepared: 0,
                    nb_missing: 0,
                    color: '',
                },

                loadedOnce: false,
            }
        },

        created() {
            Echo.private(`employees`)
                .listen('OrderUpdatedEvent', (e) => {
                    if (this.order && e.order.id == this.order.id) {
                        this.loadStateFromServer();
                    }
                });
        },

        async mounted() {
            this.loadStateFromServer();
        },

        methods: {
            async loadStateFromServer() {
                const data = await this.load(`/espace-employes/commandes/preparation/commande/${this.orderId}/json`);
                if (data) {
                    this.order = data.order ? data.order : {};
                    this.productsByMainCategory = data.productsByMainCategory;
                    this.cancellationReasons = data.cancellationReasons;
                    this.progressCounters = data.progressCounters;
                }
                this.loadedOnce = true;
            },
        },

        computed: {
            orderHaveToBePrepared() {
                return this.order.preparable;
            },

            orderHaveToBePreparedByUnit() {
                return this.order.customer.meta.production_preparation_mode === "Préparation toujours à l'unité";
            },

            productsEntriesBySku() {
                let productsEntries = {};
                if (this.productsByMainCategory) {
                    for (const index in this.productsByMainCategory) {
                        productsEntries = {
                            ...productsEntries,
                            ...this.productsByMainCategory[index].products,
                        };
                    }
                }
                return productsEntries;
            },

            orderDone,
        },
    }
</script>
